import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CartItem,
  DeliveryAddress,
  FulfillmentMethod,
  WSServerMessage,
} from '@cartiq/shared';
import type { ChatTurn } from '../types';
import { API_URL } from '../config';
import { useWebSocket } from './useWebSocket';

const HISTORY_KEY_PREFIX = 'cartiq:history:';

function uid(): string {
  return `t_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// The model emits inline ACTION:* tokens (parsed server-side into product/checkout
// cards). They must never render as chat text, so strip them from streamed copy.
function stripActionTokens(text: string): string {
  return text
    // SAVE_CONTACT-style tokens carry a {...} JSON payload that may contain spaces.
    .replace(/ACTION:[A-Z_]+:\s*\{[^}\n]*\}/gi, '')
    // Any ACTION token: well-formed, inline, or malformed (e.g. ACTION:SHOW_P286-ef5e).
    // Runs to the next whitespace, so adjacent prose is preserved.
    .replace(/ACTION:\S*/g, '')
    // A partial token still arriving at the tail of the stream buffer.
    .replace(/ACTION:?\S*$/, '')
    // Tidy whitespace left behind by removed tokens.
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '');
}

function loadHistory(slug: string): ChatTurn[] {
  try {
    const raw = sessionStorage.getItem(`${HISTORY_KEY_PREFIX}${slug}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatTurn[];
    // Never resume a turn mid-stream.
    return parsed.map((t) => ({ ...t, streaming: false }));
  } catch {
    return [];
  }
}

function persistHistory(slug: string, turns: ChatTurn[]) {
  try {
    sessionStorage.setItem(`${HISTORY_KEY_PREFIX}${slug}`, JSON.stringify(turns));
  } catch {
    // sessionStorage may be unavailable; chat still works in-memory.
  }
}

interface UseChatResult {
  turns: ChatTurn[];
  cart: CartItem[];
  awaitingReply: boolean;
  connectionState: ReturnType<typeof useWebSocket>['connectionState'];
  sendUserMessage: (content: string) => void;
  addToCart: (productId: string, variantId?: string) => void;
  updateCartItem: (productId: string, quantity: number, variantId?: string) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  checkout: (details: CheckoutDetails) => void;
}

export interface CheckoutDetails {
  customerEmail?: string;
  customerName?: string;
  customerPhone?: string;
  /** ISO 3166-1 alpha-2 — drives payment provider routing. */
  customerCountry?: string;
  /** Chosen delivery destination slug (when the store has location fees). */
  deliveryLocationSlug?: string;
  fulfillmentMethod?: FulfillmentMethod;
  deliveryAddress?: DeliveryAddress;
  deliveryInstructions?: string;
}

export function useChat(slug: string, enabled: boolean): UseChatResult {
  const { connectionState, sessionId, lastMessage, send } = useWebSocket(slug, enabled);

  const [turns, setTurns] = useState<ChatTurn[]>(() => loadHistory(slug));
  const [cart, setCart] = useState<CartItem[]>([]);
  const [awaitingReply, setAwaitingReply] = useState(false);

  // Track the assistant turn currently being assembled from stream chunks.
  const activeAssistantId = useRef<string | null>(null);
  const handledRef = useRef<WSServerMessage | null>(null);
  // Orders already reconciled (or actively polling) so the effect doesn't restart.
  const reconcilingRef = useRef<Set<string>>(new Set());
  // Stops in-flight reconcile polls on unmount (not on every turns change).
  const reconcileAliveRef = useRef(true);
  useEffect(() => {
    reconcileAliveRef.current = true;
    return () => {
      reconcileAliveRef.current = false;
    };
  }, []);

  // Ensure an assistant turn exists to receive content; return its id.
  const ensureAssistantTurn = useCallback((): string => {
    if (activeAssistantId.current) return activeAssistantId.current;
    const id = uid();
    activeAssistantId.current = id;
    setTurns((prev) => [
      ...prev,
      { id, role: 'assistant', text: '', blocks: [], streaming: true, createdAt: Date.now() },
    ]);
    return id;
  }, []);

  const patchTurn = useCallback((id: string, patch: (t: ChatTurn) => ChatTurn) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? patch(t) : t)));
  }, []);

  // Swap a settled checkout card for its confirmed state in place — used when we
  // discover (by polling) that a paid order's order_confirmed WS push was missed.
  const confirmCheckout = useCallback(
    (orderId: string, reference: string) => {
      setTurns((prev) =>
        prev.map((t) => ({
          ...t,
          blocks: t.blocks.map((b) =>
            b.kind === 'checkout' && b.orderId === orderId
              ? { kind: 'order_confirmed', orderId, reference }
              : b,
          ),
        })),
      );
      setCart([]);
    },
    [],
  );

  useEffect(() => {
    if (!lastMessage || lastMessage === handledRef.current) return;
    handledRef.current = lastMessage;
    const msg = lastMessage;

    switch (msg.type) {
      case 'welcome': {
        // The discovery hub (WelcomeScreen) owns the empty-conversation state:
        // it shows the greeting alongside suggested prompts, a featured-products
        // rail, and categories. Seeding a plain greeting turn here would make
        // turns.length > 0 and permanently shadow that hub, so we intentionally
        // do NOT inject a turn for the welcome message.
        break;
      }
      case 'stream_chunk': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => {
          const text = t.text + msg.content;
          const visible = stripActionTokens(text);
          const blocks = [...t.blocks];
          const last = blocks[blocks.length - 1];
          if (last && last.kind === 'text') {
            blocks[blocks.length - 1] = { kind: 'text', text: visible };
          } else {
            blocks.push({ kind: 'text', text: visible });
          }
          // Keep raw `text` for history/debugging; render from stripped blocks.
          return { ...t, text, blocks, streaming: true };
        });
        break;
      }
      case 'stream_end': {
        const id = activeAssistantId.current;
        if (id) patchTurn(id, (t) => ({ ...t, streaming: false }));
        activeAssistantId.current = null;
        setAwaitingReply(false);
        break;
      }
      case 'product_card': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => ({
          ...t,
          blocks: [...t.blocks, { kind: 'products', products: [msg.product] }],
        }));
        break;
      }
      case 'product_cards': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => ({
          ...t,
          blocks: [...t.blocks, { kind: 'products', products: msg.products }],
        }));
        break;
      }
      case 'availability_card': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => ({
          ...t,
          blocks: [
            ...t.blocks,
            {
              kind: 'availability',
              offeringId: msg.offeringId,
              offeringName: msg.offeringName,
              slots: msg.slots,
            },
          ],
        }));
        break;
      }
      case 'booking_card': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => ({
          ...t,
          blocks: [
            ...t.blocks,
            { kind: 'booking', booking: msg.booking, checkoutUrl: msg.checkoutUrl },
          ],
        }));
        break;
      }
      case 'cart_update': {
        setCart(msg.cart);
        break;
      }
      case 'checkout_link': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => ({
          ...t,
          streaming: false,
          blocks: [
            ...t.blocks,
            {
              kind: 'checkout',
              url: msg.url,
              orderId: msg.orderId,
              provider: msg.provider,
              total: msg.total,
              currency: msg.currency,
            },
          ],
        }));
        activeAssistantId.current = null;
        // The payment URL only exists after this round-trip, so it can't be opened
        // on the original Pay click. Navigate straight to it now — one click total.
        // The CheckoutCard stays rendered as a fallback (restored from sessionStorage
        // when the provider redirects back) in case the redirect is blocked.
        window.location.assign(msg.url);
        break;
      }
      case 'order_confirmed': {
        const id = ensureAssistantTurn();
        setAwaitingReply(false);
        patchTurn(id, (t) => ({
          ...t,
          streaming: false,
          blocks: [
            ...t.blocks,
            { kind: 'order_confirmed', orderId: msg.orderId, reference: msg.reference },
          ],
        }));
        activeAssistantId.current = null;
        setCart([]);
        break;
      }
      case 'agent_message': {
        // A human team member replied during a handoff — render it as a settled
        // assistant-style turn, attributed to the agent.
        activeAssistantId.current = null;
        setAwaitingReply(false);
        setTurns((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            text: msg.content,
            blocks: [{ kind: 'text', text: msg.content }],
            agent: true,
            createdAt: Date.now(),
          },
        ]);
        break;
      }
      case 'handoff': {
        setTurns((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            text:
              msg.state === 'human'
                ? `${msg.agentName ?? 'A team member'} has joined the chat.`
                : 'You’re back with the AI assistant.',
            blocks: [],
            notice: true,
            createdAt: Date.now(),
          },
        ]);
        break;
      }
      case 'error': {
        activeAssistantId.current = null;
        setAwaitingReply(false);
        setTurns((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            text: msg.message,
            blocks: [{ kind: 'text', text: msg.message }],
            isError: true,
            createdAt: Date.now(),
          },
        ]);
        break;
      }
      default:
        break;
    }
  }, [lastMessage, ensureAssistantTurn, patchTurn]);

  // Persist history whenever turns settle.
  useEffect(() => {
    persistHistory(slug, turns);
  }, [slug, turns]);

  // Reconcile stale checkout cards against the server. When the shopper is
  // redirected to the hosted payment page, the chat socket (and its conversation)
  // is gone by the time the payment webhook fires, so the order_confirmed push is
  // never delivered — on return, the restored history still shows "Pay Now" for an
  // order that's already paid. Poll each unconfirmed checkout's order and swap the
  // card for the confirmed state once the payment lands.
  useEffect(() => {
    const pending = turns
      .filter((t) => !t.blocks.some((b) => b.kind === 'order_confirmed'))
      .flatMap((t) => t.blocks)
      .filter((b) => b.kind === 'checkout' && b.orderId)
      .map((b) => (b as Extract<typeof b, { kind: 'checkout' }>).orderId!)
      .filter((orderId) => !reconcilingRef.current.has(orderId));

    if (pending.length === 0) return;

    for (const orderId of pending) {
      reconcilingRef.current.add(orderId);
      let attempts = 0;
      const poll = async () => {
        if (!reconcileAliveRef.current) return;
        attempts += 1;
        try {
          const res = await fetch(
            `${API_URL}/api/stores/${encodeURIComponent(slug)}/orders/${encodeURIComponent(orderId)}/status`,
          );
          if (res.ok) {
            const json = await res.json();
            const data = json?.data ?? json;
            // The order is settled once payment is collected (paid) or beyond
            // (refund_pending/refunded all imply the charge went through).
            if (data?.paymentStatus && data.paymentStatus !== 'pending' && data.paymentStatus !== 'failed') {
              if (reconcileAliveRef.current) confirmCheckout(orderId, data.reference ?? '');
              return;
            }
          }
        } catch {
          // Network hiccup — fall through and retry.
        }
        // Webhook may lag the redirect; retry a few times, then give up and let a
        // later trigger (e.g. a new checkout block) re-arm reconciliation.
        if (attempts < 6 && reconcileAliveRef.current) {
          setTimeout(poll, 3000);
        } else {
          reconcilingRef.current.delete(orderId);
        }
      };
      void poll();
    }
  }, [slug, turns, confirmCheckout]);

  const sendUserMessage = useCallback(
    (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;
      setTurns((prev) => [
        ...prev,
        {
          id: uid(),
          role: 'user',
          text: trimmed,
          blocks: [{ kind: 'text', text: trimmed }],
          createdAt: Date.now(),
        },
      ]);
      setAwaitingReply(true);
      activeAssistantId.current = null;
      send({ type: 'message', content: trimmed, sessionId });
    },
    [send, sessionId],
  );

  const addToCart = useCallback(
    (productId: string, variantId?: string) => {
      send({ type: 'add_to_cart', productId, quantity: 1, variantId });
    },
    [send],
  );

  const updateCartItem = useCallback(
    (productId: string, quantity: number, variantId?: string) => {
      send({ type: 'update_cart_item', productId, quantity, variantId });
    },
    [send],
  );

  const removeFromCart = useCallback(
    (productId: string, variantId?: string) => {
      send({ type: 'remove_from_cart', productId, variantId });
    },
    [send],
  );

  const checkout = useCallback(
    (details: CheckoutDetails) => {
      send({
        type: 'checkout',
        customerEmail: details.customerEmail,
        customerName: details.customerName,
        customerPhone: details.customerPhone,
        customerCountry: details.customerCountry,
        deliveryLocationSlug: details.deliveryLocationSlug,
        fulfillmentMethod: details.fulfillmentMethod,
        deliveryAddress: details.deliveryAddress,
        deliveryInstructions: details.deliveryInstructions,
      });
    },
    [send],
  );

  return {
    turns,
    cart,
    awaitingReply,
    connectionState,
    sendUserMessage,
    addToCart,
    updateCartItem,
    removeFromCart,
    checkout,
  };
}
